// @flow strict

import * as React from 'react'
import cx from 'classnames'
import { useState, useEffect, useRef } from 'react'
import { throttle } from 'throttle-debounce'
import { type PropsWithChildren } from '../../../utils/typeUtils'
import { type NavigationItemProps, NavigationItem } from './NavigationItem'

// Components
import AccessibilityText from '../../../atoms/AccessibilityText'

// Types
export type NavStyles = { [k: string]: string }
type OnClickHandler = (SyntheticMouseEvent<*>) => mixed

type Placement = 'minimal' | 'full'

type ToggleButtonProps = {
  children: React.ChildrenArray<
    string | React.Element<'svg' | typeof AccessibilityText>,
  >,
  onClick: OnClickHandler,
  placement: Placement,
  navigationStyles: NavStyles,
}

type MinimalNavigationProps = {
  onClick: OnClickHandler,
  navigationStyles: NavStyles,
  logoHref?: string,
}

type FullNavigationProps = {
  isNavigationVisible: boolean,
  setIsNavigationVisible: OnClickHandler,
  navigationStyles: NavStyles,
  activeContext: 'personal' | 'business',
  navigationListStyles: NavStyles,
  navLinks: NavigationItemProps[],
  callToAction?: React.Node,
  logoHref?: string,
}

const useHasScrolledAwayFromTop = () => {
  const [headerScrolled, setHeaderScrolled] = useState(false)
  const ref = useRef()

  useEffect(() => {
    document.addEventListener(
      'scroll',
      throttle(
        120,
        false,
        e => {
          const target = e.currentTarget || e.target
          const { scrollTop } = target.documentElement || target.body

          if (ref.current != null && target) {
            if (ref.current.getBoundingClientRect().height / 2 < scrollTop) {
              setHeaderScrolled(true)
            } else if (
              ref.current &&
              ref.current.getBoundingClientRect().height / 2 >= scrollTop
            ) {
              setHeaderScrolled(false)
            }
          }
        },
        false,
      ),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return [headerScrolled, ref]
}

type LogoProps = {
  placement: Placement,
  navigationStyles: NavStyles,
  href?: string,
}

const Logo = ({ placement, navigationStyles, href }: LogoProps) => {
  const classes =
    placement === 'minimal'
      ? navigationStyles.logoMinimal
      : navigationStyles.logoFull
  return (
    <a href={href != null ? href : '/'} className={classes}>
      Monzo bank
    </a>
  )
}

const ToggleButton = ({
  children,
  onClick,
  placement,
  navigationStyles,
}: ToggleButtonProps) => {
  const classes =
    placement === 'minimal'
      ? navigationStyles.toggleMinimal
      : navigationStyles.toggleFull
  return (
    <button className={classes} onClick={onClick} data-testid="ToggleButton">
      {children}
    </button>
  )
}

export const MinimalNavigation = ({
  onClick,
  navigationStyles,
  logoHref,
}: MinimalNavigationProps) => {
  const [headerScrolled, ref] = useHasScrolledAwayFromTop()
  return (
    <div
      ref={ref}
      className={cx(
        navigationStyles.minimal,
        headerScrolled
          ? navigationStyles.scrolledHeader
          : navigationStyles.topHeader,
      )}
      data-testid="MinimalNavigation"
    >
      <div className={navigationStyles.container}>
        <div className={navigationStyles.header}>
          <Logo
            placement="minimal"
            navigationStyles={navigationStyles}
            href={logoHref}
          />
          <ToggleButton
            onClick={onClick}
            placement="minimal"
            navigationStyles={navigationStyles}
          >
            Menu
            <svg width="18" height="14">
              <use href="#menu-open" xlinkHref="#menu-open" />
            </svg>
          </ToggleButton>
        </div>
      </div>
    </div>
  )
}

export const FullNavigation = ({
  isNavigationVisible,
  setIsNavigationVisible,
  navigationStyles,
  navigationListStyles,
  navLinks,
  callToAction,
  activeContext,
  children,
  logoHref,
}: PropsWithChildren<
  FullNavigationProps,
  React.Element<typeof NavigationItem>[],
>) => {
  const [headerScrolled, ref] = useHasScrolledAwayFromTop()
  return (
    <header
      ref={ref}
      className={cx(
        isNavigationVisible
          ? navigationStyles.fullVisible
          : navigationStyles.fullHidden,
        headerScrolled && !isNavigationVisible
          ? navigationStyles.scrolledHeader
          : navigationStyles.topHeader,
      )}
      data-testid="FullNavigation"
    >
      <div className={navigationStyles.container}>
        <div
          className={cx(
            navigationStyles.header,
            navigationListStyles.rightPipe,
          )}
        >
          <Logo
            placement="full"
            navigationStyles={navigationStyles}
            href={logoHref}
          />
          <ToggleButton
            onClick={setIsNavigationVisible}
            placement="full"
            navigationStyles={navigationStyles}
          >
            <AccessibilityText>Close Menu</AccessibilityText>
            <svg width="14" height="14">
              <use href="#menu-close" xlinkHref="#menu-close" />
            </svg>
          </ToggleButton>
        </div>
        <nav className={navigationListStyles.wrapper}>
          <ul
            className={cx(
              navigationListStyles.list,
              navigationListStyles.contextList,
            )}
          >
            <li
              className={cx(
                navigationListStyles.item,
                activeContext === 'personal' &&
                  navigationListStyles.highlightedContext,
              )}
            >
              <a href={'/'} className={navigationListStyles.link}>
                Personal
              </a>
            </li>
            <li
              className={cx(
                navigationListStyles.item,
                activeContext === 'business' &&
                  navigationListStyles.highlightedContext,
              )}
            >
              <a href={'/i/business'} className={navigationListStyles.link}>
                Business
              </a>
            </li>
          </ul>

          <hr className={navigationListStyles.separator} />
          <ul className={navigationListStyles.list}>{children}</ul>
        </nav>
        <div className={navigationStyles.footer}>{callToAction}</div>
      </div>
    </header>
  )
}

// Defining SVGs inline as we need to change their fill colours
export const SvgDefs = ({
  navigationStyles,
  hasDropdowns,
}: {
  navigationStyles: NavStyles,
  hasDropdowns: boolean,
}) => (
  <svg className={navigationStyles.icons}>
    <defs>
      <symbol
        id="menu-close"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M.293.293a1 1 0 0 1 1.414 0L7 5.585 12.293.293a1 1 0 0 1 1.32-.083l.094.083a1 1 0 0 1 0 1.414L8.415 7l5.292 5.293a1 1 0 0 1 .083 1.32l-.083.094a1 1 0 0 1-1.414 0L7 8.415l-5.293 5.292a1 1 0 0 1-1.32.083l-.094-.083a1 1 0 0 1 0-1.414L5.585 7 .293 1.707A1 1 0 0 1 .21.387z" />
      </symbol>
      <symbol
        id="menu-open"
        width="18"
        height="14"
        viewBox="0 0 18 14"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g>
          <path d="M16.5 11a1.5 1.5 0 0 1 0 3h-15a1.5 1.5 0 0 1 0-3h15zm0-5.5a1.5 1.5 0 0 1 0 3h-15a1.5 1.5 0 0 1 0-3h15zm0-5.5a1.5 1.5 0 0 1 0 3h-15a1.5 1.5 0 0 1 0-3h15z" />
        </g>
      </symbol>
    </defs>
  </svg>
)
